/**
 * Monthly Timesheet View
 * Displays a calendar grid with hours logged per day
 */

import React, { useMemo, useState } from 'react';
import { Modal, Spin, Empty, Tooltip } from 'antd';
import { useDailySummaries } from '@/hooks/useTimesheet';
import type { DailySummary } from '@/api/types/timesheet.types';
import dayjs from 'dayjs';
import './MonthlyView.css';

interface MonthlyViewProps {
  year: number;
  month: number; // 1-12
  projectId?: string;
  showAll?: boolean; // true = all statuses, false = approved only
  canEdit?: boolean; // Can user edit timesheets?
  onDayClick?: (date: string) => void;
}

export const MonthlyView: React.FC<MonthlyViewProps> = ({
  year,
  month,
  projectId,
  showAll = false,
  canEdit: _canEdit = false,
  onDayClick,
}) => {
  const [selectedDate, setSelectedDate] = useState<string | null>(null);

  // Fetch daily summaries for the entire month
  const dateFrom = dayjs(new Date(year, month - 1, 1)).format('YYYY-MM-DD');
  const dateTo = dayjs(new Date(year, month, 0)).format('YYYY-MM-DD');

  const { data, isLoading, error } = useDailySummaries({
    project_id: projectId,
    date_from: dateFrom,
    date_to: dateTo,
  });

  // Generate calendar grid
  const calendarGrid = useMemo(() => {
    const firstDay = new Date(year, month - 1, 1);
    const lastDay = new Date(year, month, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay(); // 0=Sunday

    // Adjust to Monday=0, Sunday=6 (ISO week)
    const startDay = startDayOfWeek === 0 ? 6 : startDayOfWeek - 1;

    const grid: (number | null)[][] = [];
    let week: (number | null)[] = [];

    // Fill empty cells before month starts
    for (let i = 0; i < startDay; i++) {
      week.push(null);
    }

    // Fill days
    for (let day = 1; day <= daysInMonth; day++) {
      week.push(day);
      if (week.length === 7) {
        grid.push(week);
        week = [];
      }
    }

    // Fill remaining empty cells
    while (week.length < 7 && week.length > 0) {
      week.push(null);
    }
    if (week.length > 0) grid.push(week);

    return grid;
  }, [year, month]);

  // Map summaries by date
  const summariesByDate = useMemo(() => {
    const map = new Map<string, DailySummary>();
    data?.items.forEach((summary) => {
      map.set(summary.summary_date, summary);
    });
    return map;
  }, [data]);

  // Calculate totals
  const totals = useMemo(() => {
    if (!data?.items || data.items.length === 0) {
      return {
        totalHours: 0,
        billableHours: 0,
        nonBillableHours: 0,
        approvedHours: 0,
        pendingCount: 0,
      };
    }

    return data.items.reduce(
      (acc, summary) => {
        acc.totalHours += summary.total_hours_logged;
        acc.billableHours += summary.billable_hours_logged;
        acc.nonBillableHours += summary.non_billable_hours_logged;
        acc.approvedHours += summary.approved_hours_logged;
        acc.pendingCount += summary.number_of_pending_entries;
        return acc;
      },
      {
        totalHours: 0,
        billableHours: 0,
        nonBillableHours: 0,
        approvedHours: 0,
        pendingCount: 0,
      }
    );
  }, [data]);

  const handleDayClick = (day: number) => {
    const date = dayjs(new Date(year, month - 1, day)).format('YYYY-MM-DD');
    setSelectedDate(date);
    if (onDayClick) {
      onDayClick(date);
    }
  };

  const renderCell = (day: number | null) => {
    if (day === null) {
      return <div className="timesheet-cell timesheet-cell-empty" />;
    }

    const date = dayjs(new Date(year, month - 1, day)).format('YYYY-MM-DD');
    const summary = summariesByDate.get(date);
    const today = dayjs().format('YYYY-MM-DD');
    const isToday = date === today;
    const isWeekend = new Date(year, month - 1, day).getDay() % 6 === 0; // 0=Sun, 6=Sat

    // Filter based on showAll
    const hours = summary
      ? showAll
        ? summary.total_hours_logged
        : summary.approved_hours_logged
      : 0;

    const hasPending = summary && summary.number_of_pending_entries > 0;
    const hasData = hours > 0;

    const cellClass = [
      'timesheet-cell',
      isToday && 'timesheet-cell-today',
      isWeekend && 'timesheet-cell-weekend',
      hasData && 'timesheet-cell-has-data',
      hasPending && 'timesheet-cell-pending',
    ]
      .filter(Boolean)
      .join(' ');

    const tooltipContent = summary ? (
      <div>
        <div>
          <strong>Total:</strong> {summary.total_hours_logged.toFixed(1)}h
        </div>
        <div>
          <strong>Billable:</strong> {summary.billable_hours_logged.toFixed(1)}h
        </div>
        <div>
          <strong>Non-billable:</strong>{' '}
          {summary.non_billable_hours_logged.toFixed(1)}h
        </div>
        <div>
          <strong>Approved:</strong> {summary.approved_hours_logged.toFixed(1)}h
        </div>
        {hasPending && (
          <div className="timesheet-pending-badge">
            <strong>Pending:</strong> {summary.number_of_pending_entries}{' '}
            {summary.number_of_pending_entries === 1 ? 'entry' : 'entries'}
          </div>
        )}
      </div>
    ) : (
      <div>No timesheets logged</div>
    );

    return (
      <Tooltip title={tooltipContent} placement="top">
        <div
          className={cellClass}
          onClick={() => hasData && handleDayClick(day)}
        >
          <div className="timesheet-cell-day">{day}</div>
          {hasData && (
            <div className="timesheet-cell-hours">
              {hours.toFixed(1)}
              <span className="timesheet-cell-hours-unit">h</span>
            </div>
          )}
          {hasPending && <div className="timesheet-cell-pending-dot" />}
        </div>
      </Tooltip>
    );
  };

  if (isLoading) {
    return (
      <div className="timesheet-monthly-loading">
        <Spin size="large" tip="Loading timesheet..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="timesheet-monthly-error">
        <Empty description="Failed to load timesheet data" />
      </div>
    );
  }

  return (
    <div className="timesheet-monthly">
      {/* Totals Summary */}
      <div className="timesheet-monthly-summary">
        <div className="timesheet-monthly-totals">
          <div className="timesheet-total-item">
            <span className="timesheet-total-label">Total Hours:</span>
            <span className="timesheet-total-value">
              {totals.totalHours.toFixed(1)}h
            </span>
          </div>
          <div className="timesheet-total-item">
            <span className="timesheet-total-label">Billable:</span>
            <span className="timesheet-total-value timesheet-total-billable">
              {totals.billableHours.toFixed(1)}h
            </span>
          </div>
          <div className="timesheet-total-item">
            <span className="timesheet-total-label">Non-billable:</span>
            <span className="timesheet-total-value timesheet-total-non-billable">
              {totals.nonBillableHours.toFixed(1)}h
            </span>
          </div>
          <div className="timesheet-total-item">
            <span className="timesheet-total-label">Approved:</span>
            <span className="timesheet-total-value timesheet-total-approved">
              {totals.approvedHours.toFixed(1)}h
            </span>
          </div>
          {totals.pendingCount > 0 && (
            <div className="timesheet-total-item">
              <span className="timesheet-total-label">Pending:</span>
              <span className="timesheet-total-value timesheet-total-pending">
                {totals.pendingCount}{' '}
                {totals.pendingCount === 1 ? 'entry' : 'entries'}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="timesheet-calendar">
        {/* Header */}
        <div className="timesheet-calendar-header">
          {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((dayName) => (
            <div key={dayName} className="timesheet-calendar-header-cell">
              {dayName}
            </div>
          ))}
        </div>

        {/* Grid */}
        <div className="timesheet-calendar-grid">
          {calendarGrid.map((week, weekIndex) => (
            <div key={weekIndex} className="timesheet-calendar-week">
              {week.map((day, dayIndex) => (
                <div key={dayIndex}>{renderCell(day)}</div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {/* Day Detail Modal (placeholder for now) */}
      <Modal
        title={`Timesheet - ${selectedDate}`}
        open={!!selectedDate}
        onCancel={() => setSelectedDate(null)}
        footer={null}
        width={800}
      >
        <div>
          <p>Detailed view for {selectedDate}</p>
          <p>TODO: Implement daily breakdown</p>
        </div>
      </Modal>
    </div>
  );
};
